<template>
  <div
    style="width: 100vw; height: 100vh; position: relative; overflow: hidden"
  >
    <div
      style="
        width: 100%;
        height: 100%;
        display: flex;
        flex: 1;
        flex-direction: column;
      "
    >
      <div
        id="map"
        ref="mapContainer"
        style="width: 100%; height: 100%; flex: 1"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Coordinate from "@/domains/coordinates/coordinate";
import { useMapStore, type MarkerData } from "@/stores/mapStore";
import "@maplibre/maplibre-gl-leaflet";
import L from "leaflet";
import "leaflet-hotline";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet/dist/leaflet.css";
import "maplibre-gl/dist/maplibre-gl.css";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useDisplay } from "vuetify";

const mapStore = useMapStore();
const { width } = useDisplay();

// ---------------------------------------------------------------------------- //
// ------------------------------- Map Controls ------------------------------- //
// ---------------------------------------------------------------------------- //

const computedCenter = computed(() => mapStore.getCenter);
const mapContainer = ref(null);
let map: L.Map | null = null;

/**
 * Initializes the map component.
 *
 * This function is called after the component has mounted.
 *
 * It creates a Leaflet map instance with the given options and adds a tile layer to it.
 * It also creates a marker cluster group and adds it to the map.
 *
 * The marker cluster group is customized to display the number of child markers in the cluster
 * and to change its color based on the number of markers in the cluster.
 *
 * @returns {void}
 */
const initializeMap = () => {
  if (!mapContainer.value) return;
  map = L.map(mapContainer.value, {
    maxZoom: 19,
    minZoom: 3,
    zoomControl: false,
  }).setView(computedCenter.value.asLatLng, 7);

  map.on("moveend", () => {
    const center = map?.getCenter();
    if (!center) return;
    mapStore.pannedCenter = new Coordinate({
      latitude: center.lat,
      longitude: center.lng,
    });
  });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  L.maplibreGL({
    style: "https://magic.appstack.me/osm_power_trip.json",
    maxZoom: 19,
  }).addTo(map);

  markerClusterGroup = L.markerClusterGroup({
    iconCreateFunction: function (cluster) {
      const count = cluster.getChildCount();
      let color = "tango";
      if (count < 5) {
        color = "#ed924e"; // Lightest orange
      } else if (count < 10) {
        color = "#f97316"; // Light orange
      } else if (count < 20) {
        color = "#e06609"; // Medium orange
      } else if (count < 50) {
        color = "#b84f0a"; // Burnt orange
      } else if (count < 100) {
        color = "#8e3d0c"; // Deep rust
      } else {
        color = "#652c0b"; // Darkest orange
      }

      return L.divIcon({
        html: `<div style="background-color: ${color}; border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; color: white;">${count}</div>`,
        className: "marker-cluster",
        iconSize: L.point(30, 30),
      });
    },
  });
  if (markerClusterGroup) map.addLayer(markerClusterGroup);
  // update now initialized map. Called here rather than in onMounted to prevent possible race condition.
  updateClusteredMarkers();
  updatePolylines();
};

/**
 * Re-center the map to show all points in the given list.
 * @param pointsList List of points to show on the map.
 */
const reCenterMap = (pointsList: [number, number][]) => {
  // get map padding (40% in pixels)
  const options = {
    paddingTopLeft: L.point((width.value / 100) * 40, 0),
  };

  // center map
  if (pointsList.length) map?.fitBounds(pointsList, options);
};

// ---------------------------------------------------------------------------- //
// ----------------------------- Markers Controls ----------------------------- //
// ---------------------------------------------------------------------------- //

/** Leaflet default marker */
const defaultMarkerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png";

const createCustomMarker = (markerData: MarkerData): L.Icon => {
  const customIcon = L.icon({
    iconUrl: markerData.iconSrc ?? defaultMarkerSrc,
    iconSize: [41, 41], // Size of the icon
    iconAnchor: [21, 41], // Point of the icon which will correspond to marker's location
    popupAnchor: [1, -34], // Point from which the popup should open relative to the iconAnchor
    shadowUrl:
      "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png", // Default Leaflet marker shadow
    shadowSize: [41, 41], // Size of the shadow
    shadowAnchor: [12, 41], // Point of the shadow which will correspond to marker's location
    className: "custom-marker", // Add a custom class to the marker
  });
  return customIcon;
};

let markerClusterGroup: L.MarkerClusterGroup | null = null;
let markers: L.Marker[] = [];
const clusteredMarkers = computed(() => mapStore.getClusteredMarkers);

/**
 * Clears the existing markers and adds new ones based on the filtered chargers in the Vuex store.
 *
 * This function is called whenever the filtered chargers change in the Vuex store.
 *
 * @remarks
 * The markers are added to a Leaflet {@link https://leafletjs.com/reference-1.7.1.html#markercluster marker cluster group}.
 * This allows for efficient clustering of nearby markers.
 *
 * Each marker is given a custom icon with a different color based on the number of chargers at that location.
 * Clicking on a marker will log a message to the console indicating which charger was clicked.
 */
const updateClusteredMarkers = () => {
  markerClusterGroup?.clearLayers();
  markers = [];

  clusteredMarkers.value.forEach((markerData) => {
    const customIcon = createCustomMarker(markerData);

    const marker = L.marker([markerData.latitude, markerData.longitude], {
      icon: customIcon,
    });
    if (markerData.onClick) marker.on("click", markerData.onClick);
    markers.push(marker);
    markerClusterGroup?.addLayer(marker);
  });
};

let individualMarkers: L.Marker[] = [];
const individualMarkersToRender = computed(() => mapStore.getMarkers);

const updateIndividualMarkers = () => {
  individualMarkers.forEach((marker) => map?.removeLayer(marker));
  individualMarkers = [];

  individualMarkersToRender.value.forEach((markerData) => {
    const locationCustomIcon = createCustomMarker(markerData);
    const marker = L.marker([markerData.latitude, markerData.longitude], {
      icon: locationCustomIcon,
    });
    if (markerData.onClick) marker.on("click", markerData.onClick);
    individualMarkers.push(marker);
    map?.addLayer(marker);
  });
};

const markersWithRotationData = computed(() => mapStore.getMarkersWithRotation);
let markersWithRotation: L.Marker[] = [];
const updateIndividualMarkersWithRotation = () => {
  markersWithRotation.forEach((marker) => map?.removeLayer(marker));
  markersWithRotation = [];

  markersWithRotationData.value.forEach((markerData) => {
    const customIcon = L.divIcon({
      html: `<img src="${markerData.iconSrc ?? defaultMarkerSrc}" style="transform: rotate(${markerData.rotation ?? 0}deg);">`,
      className: "vehicle-icon",
      iconSize: [50, 50],
      iconAnchor: [25, 25],
    });
    const marker = L.marker([markerData.latitude, markerData.longitude], {
      icon: customIcon,
    });
    if (markerData.onClick) marker.on("click", markerData.onClick);
    markersWithRotation.push(marker);
    map?.addLayer(marker);
  });
};

// ---------------------------------------------------------------------------- //
// ----------------------------- Polyline Controls ---------------------------- //
// ---------------------------------------------------------------------------- //

const polylinesToRender = computed(() => mapStore.getGradientPolylines);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let polylines: any[] = [];

const updatePolylines = () => {
  polylines.forEach((polyline) => map?.removeLayer(polyline));
  polylines = [];

  // options to be used on each instance of the polyline
  const hotlineOptions = {
    min: 0,
    max: 1,
    palette: {
      0.2: "#F44336",
      0.3: "#9C27B0",
      0.4: "#0D47A1",
      0.9: "#2196F3",
    },
    outlineColor: "#FFFFFF",
    weight: 5,
    outlineWidth: 1,
  };

  if (polylinesToRender.value) {
    // create a polyline for each step
    polylinesToRender.value.forEach((threeDimensionalLatlngs) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const polylineLayer = L.hotline(threeDimensionalLatlngs, hotlineOptions);
      if (map) polylineLayer.addTo(map);
      polylines.push(polylineLayer);
    });
  }
};

function resizeMap() {
  // timeout used to let browser handle resize animation before resetting
  setTimeout(() => {
    map?.invalidateSize();
  }, 200);
}

onMounted(async () => {
  window.addEventListener("resize", resizeMap);
  const LeafletHotline = await import("leaflet-hotline");
  LeafletHotline.default(L);
  initializeMap();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeMap);
});

watch(
  clusteredMarkers,
  () => {
    updateClusteredMarkers();
  },
  { deep: true },
);

watch(
  markersWithRotationData,
  () => {
    updateIndividualMarkersWithRotation();
  },
  { deep: true },
);

watch(
  individualMarkersToRender,
  () => {
    updateIndividualMarkers();
  },
  { deep: true },
);

watch(
  polylinesToRender,
  () => {
    updatePolylines();
    if (polylinesToRender.value) {
      const points = polylinesToRender.value.flatMap((polyline) =>
        polyline.map(([lng, lat]) => [lng, lat] as [number, number]),
      );

      reCenterMap(points);
    }
  },
  { deep: true },
);

// ---------------------------------------------------------------------------- //
// ----------------------------- Fly to controls ------------------------------ //
// ---------------------------------------------------------------------------- //

const geoIp = computed(() => mapStore.geoIp);

watch(geoIp, () => {
  if (geoIp.value && map && !polylinesToRender.value) {
    map.flyTo(geoIp.value.asLatLng, 7);
  }
});

const flyTo = computed(() => mapStore.flyTo);

watch(flyTo, () => {
  // get map padding (40% in pixels) and zoom to 16
  const options = {
    paddingTopLeft: L.point((width.value / 100) * 40, 0),
    maxZoom: 16,
    duration: 1,
  };
  if (map && flyTo.value) {
    map.flyToBounds([flyTo.value.asLatLng], options);
  }
});
</script>

<style>
.leaflet-control-attribution {
  @media screen and (min-width: 600px) {
    padding-right: 20px;
  }
}
</style>
